import React from "react";
import { RefreshCw } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

/**
 * Subtle "Refreshed · 3 days ago" badge — fires when a publisher has edited
 * their listing within the freshness window, but the listing is no longer
 * NEW (claim is older than 28 days). Designed to be visually quieter than
 * the green NEW pill so it rewards continued maintenance without screaming.
 *
 * Returns null when the app isn't recently updated → callers can drop it
 * in unconditionally:  {app.recently_updated && <UpdatedBadge app={app} />}
 */
const UpdatedBadge = ({ app, variant = "pill" }) => {
  const { t } = useLang();
  if (!app || !app.recently_updated) return null;
  const days = app.updated_days_ago ?? 0;

  const refreshedLabel = (t?.common?.refreshedBadge) || "Updated";
  const refreshedToday = (t?.common?.refreshedToday) || "Refreshed today";
  const refreshedDaysAgoTpl =
    (t?.common?.refreshedDaysAgo) || "Refreshed {n} days ago";
  const ageLabel =
    days <= 0 ? refreshedToday : refreshedDaysAgoTpl.replace("{n}", String(days));

  if (variant === "strip") {
    return (
      <div
        data-testid={`updated-strip-${app.slug}`}
        className="inline-flex items-center gap-2 bg-sky-50 text-sky-700 border border-sky-200 px-3 py-1.5 rounded-full text-xs font-semibold mb-4"
      >
        <RefreshCw size={12} className="text-sky-500" />
        <span className="uppercase tracking-wider text-[10px] font-bold">
          {refreshedLabel}
        </span>
        <span className="text-sky-600 font-normal normal-case">
          {" · "}{ageLabel}
        </span>
      </div>
    );
  }

  // pill (default) — light slate/sky, deliberately quieter than NEW
  return (
    <span
      data-testid={`updated-badge-${app.slug}`}
      className="inline-flex items-center gap-1 bg-sky-100 text-sky-700 border border-sky-200 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
    >
      <RefreshCw size={9} className="text-sky-500" />
      {refreshedLabel}
    </span>
  );
};

export default UpdatedBadge;
